<template>
  <v-card class="exp-filter-bar mb-4" flat>
    <div class="d-flex align-center flex-wrap justify-space-between exp-filter-inner px-4 py-3">
      <!-- Title & Accent badge -->
      <div class="d-flex align-center exp-filter-title-wrap">
        <span class="exp-accent-bar mr-2"></span>
        <div class="d-flex flex-column">
          <div class="d-flex align-center">
            <span class="exp-title-text">{{ $t(title) }}</span>
            <v-chip x-small color="primary" outlined class="ml-2 font-weight-bold">
              {{ formattedMonth }}
            </v-chip>
          </div>
          <span class="exp-subtitle-text">{{ $t("income_expense") }}</span>
        </div>
      </div>

      <!-- Month Filter & Quick Jump Controls -->
      <div class="d-flex align-center flex-wrap exp-filter-actions">
        <!-- Prev month button -->
        <v-btn
          icon
          small
          outlined
          class="exp-step-btn mr-1"
          @click="stepMonth(-1)"
          :title="$t('previous')"
        >
          <v-icon small>fal fa-chevron-left</v-icon>
        </v-btn>

        <!-- Month Picker Field -->
        <div class="exp-date-field-wrap">
          <v-text-field
            v-model="date"
            hide-details
            outlined
            dense
            color="primary"
            placeholder="YYYY-MM"
            readonly
            class="exp-date-input"
            @click="modal = true"
          >
            <template v-slot:prepend-inner>
              <v-icon small color="primary" class="mr-1">fal fa-calendar-alt</v-icon>
            </template>
          </v-text-field>
        </div>

        <!-- Next month button -->
        <v-btn
          icon
          small
          outlined
          class="exp-step-btn ml-1 mr-2"
          @click="stepMonth(1)"
          :title="$t('next')"
        >
          <v-icon small>fal fa-chevron-right</v-icon>
        </v-btn>

        <!-- Current month quick reset button -->
        <v-btn
          small
          depressed
          class="exp-today-btn"
          @click="resetToCurrentMonth"
        >
          {{ $t("today") }}
        </v-btn>

        <!-- Month Picker Dialog -->
        <app-custom-dialog
          v-model="modal"
          title="month"
          width="320"
          :footer="false"
        >
          <div slot="activator">
            <v-date-picker
              v-model="date"
              type="month"
              scrollable
              color="primary"
              class="exp-picker-dialog"
            >
              <v-spacer></v-spacer>
              <v-btn
                text
                depressed
                class="px-4"
                @click="modal = false"
              >
                {{ $t("cancel") }}
              </v-btn>
              <v-btn
                color="primary"
                depressed
                class="px-4 font-weight-bold"
                @click="submit(date)"
              >
                {{ $t("ok") }}
              </v-btn>
            </v-date-picker>
          </div>
        </app-custom-dialog>
      </div>
    </div>
  </v-card>
</template>

<script>
export default {
  name: "AppCardTitle",
  props: {
    cols: {
      default: 2,
    },
    title: {
      default: "summary",
    },
  },
  data: () => ({
    date: new Date().toISOString().substr(0, 7),
    modal: false,
  }),
  computed: {
    formattedMonth() {
      if (!this.date) return "";
      const parts = this.date.split("-");
      if (parts.length === 2) {
        return `${parts[1]}/${parts[0]}`;
      }
      return this.date;
    },
  },
  methods: {
    submit(val) {
      this.$emit("filter", val);
      this.modal = false;
    },
    stepMonth(offset) {
      if (!this.date) {
        this.date = new Date().toISOString().substr(0, 7);
      }
      const [year, month] = this.date.split("-").map(Number);
      const d = new Date(year, month - 1 + offset, 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      this.date = `${y}-${m}`;
      this.submit(this.date);
    },
    resetToCurrentMonth() {
      const nowStr = new Date().toISOString().substr(0, 7);
      if (this.date !== nowStr) {
        this.date = nowStr;
        this.submit(this.date);
      }
    },
  },
};
</script>

<style scoped>
.exp-filter-bar {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 14px !important;
  box-shadow: none !important;
}

.exp-filter-inner {
  gap: 12px;
}

.exp-accent-bar {
  width: 4px;
  height: 28px;
  background: #5b39c9;
  border-radius: 4px;
}

.exp-title-text {
  font-size: 16px;
  font-weight: 700;
  color: #141021;
}

.exp-subtitle-text {
  font-size: 11px;
  color: #94a3b8;
}

.exp-filter-actions {
  gap: 6px;
}

.exp-date-field-wrap {
  width: 135px;
}

.exp-date-input ::v-deep .v-input__control {
  min-height: 36px;
}

.exp-date-input ::v-deep .v-input__slot {
  min-height: 36px !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #141021 !important;
  padding: 0 10px !important;
  cursor: pointer !important;
}

.exp-step-btn {
  border-color: #e2e8f0 !important;
  border-radius: 8px !important;
  color: #64748b !important;
  width: 36px !important;
  height: 36px !important;
}

.exp-step-btn:hover {
  border-color: #5b39c9 !important;
  color: #5b39c9 !important;
}

.exp-today-btn {
  height: 36px !important;
  border-radius: 8px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  background: #f1f5f9 !important;
  color: #475569 !important;
  border: 1px solid #e2e8f0 !important;
}

.exp-today-btn:hover {
  background: #e2e8f0 !important;
  color: #141021 !important;
}

.exp-picker-dialog {
  border-radius: 12px !important;
}

.theme--dark .exp-filter-bar {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .exp-title-text {
  color: #ffffff;
}

.theme--dark .exp-subtitle-text {
  color: #94a3b8;
}

.theme--dark .exp-date-input ::v-deep .v-input__slot {
  background: #262638 !important;
  color: #ffffff !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .exp-date-input ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .exp-date-input ::v-deep input {
  color: #ffffff !important;
}

.theme--dark .exp-step-btn {
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #94a3b8 !important;
}

.theme--dark .exp-step-btn:hover {
  border-color: #818cf8 !important;
  color: #c4b5fd !important;
}

.theme--dark .exp-today-btn {
  background: #262638 !important;
  color: #cbd5e1 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .exp-today-btn:hover {
  background: rgba(129, 140, 248, 0.2) !important;
  color: #ffffff !important;
}
</style>
